const DB_NAME = 'spyx_review_form_draft'
const DB_VERSION = 1
const STORE_NAME = 'drafts'
const OWNER_STORAGE_KEY = 'spyx_review_form_draft_owner'
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

const parseJson = raw => {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const getReviewFormDraftOwnerKey = () => {
  if (typeof window === 'undefined') return ''

  const userData = parseJson(window.localStorage.getItem('userData'))
  const userInfo = parseJson(window.localStorage.getItem('userInfo'))
  const user = parseJson(window.localStorage.getItem('user'))

  const candidates = [
    userData?.data?.email,
    userData?.data?.id,
    userData?.email,
    userData?.id,
    userInfo?.data?.email,
    userInfo?.data?.id,
    userInfo?.email,
    userInfo?.id,
    user?.email,
    user?.id
  ]

  const value = candidates.find(item => item !== undefined && item !== null && item !== '')
  return value == null ? '' : String(value)
}

const openDB = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      resolve(null)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'platform' })
      }
    }
  })

export const clearAllReviewFormDrafts = async ({ resetOwner = true } = {}) => {
  const db = await openDB()
  if (!db) {
    if (resetOwner && typeof window !== 'undefined') {
      window.localStorage.removeItem(OWNER_STORAGE_KEY)
    }
    return
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).clear()
    tx.oncomplete = () => {
      db.close()
      if (resetOwner && typeof window !== 'undefined') {
        window.localStorage.removeItem(OWNER_STORAGE_KEY)
      }
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

export const clearReviewFormDraftsForLogout = () => {
  void clearAllReviewFormDrafts()
}

const isDraftExpired = draft => {
  if (!draft?.updatedAt) return true
  return Date.now() - draft.updatedAt > DRAFT_TTL_MS
}

const purgeExpiredDrafts = async () => {
  const db = await openDB()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const request = store.openCursor()

    request.onsuccess = event => {
      const cursor = event.target.result
      if (!cursor) return

      if (isDraftExpired(cursor.value)) {
        cursor.delete()
      }
      cursor.continue()
    }

    request.onerror = () => {
      db.close()
      reject(request.error)
    }

    tx.oncomplete = () => {
      db.close()
      resolve()
    }

    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

const ensureDraftOwner = async () => {
  if (typeof window === 'undefined') return

  const currentOwner = getReviewFormDraftOwnerKey()
  const storedOwner = window.localStorage.getItem(OWNER_STORAGE_KEY)

  if (!currentOwner) {
    if (storedOwner) {
      await clearAllReviewFormDrafts({ resetOwner: true })
    }
    return
  }

  if (storedOwner && storedOwner !== currentOwner) {
    await clearAllReviewFormDrafts({ resetOwner: false })
  }

  window.localStorage.setItem(OWNER_STORAGE_KEY, currentOwner)
}

export const saveReviewFormDraft = async (platform, draft) => {
  await ensureDraftOwner()
  await purgeExpiredDrafts()

  const db = await openDB()
  if (!db) return

  const ownerKey = getReviewFormDraftOwnerKey()
  if (!ownerKey) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put({
      platform,
      ownerKey,
      account: draft.account || '',
      notes: draft.notes || '',
      screenshotUrls: draft.screenshotUrls || [],
      updatedAt: Date.now()
    })
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

export const getReviewFormDraft = async platform => {
  await ensureDraftOwner()
  await purgeExpiredDrafts()

  const db = await openDB()
  if (!db) return null

  const ownerKey = getReviewFormDraftOwnerKey()
  if (!ownerKey) return null

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const request = tx.objectStore(STORE_NAME).get(platform)
    request.onsuccess = () => {
      const result = request.result || null
      if (result && result.ownerKey && result.ownerKey !== ownerKey) {
        db.close()
        resolve(null)
        return
      }
      if (result && isDraftExpired(result)) {
        db.close()
        void clearReviewFormDraft(platform)
        resolve(null)
        return
      }
      db.close()
      resolve(result)
    }
    request.onerror = () => {
      db.close()
      reject(request.error)
    }
  })
}

export const clearReviewFormDraft = async platform => {
  const db = await openDB()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(platform)
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}
